import { useCallback, useEffect, useMemo, useState } from "react"

import { format } from "date-fns/format"

import { API_MODULE_HORIZONTAL_SIGNING, HorizontalSignCampaignCatalog } from "@l2r-front/l2r-app-city"
import { useTranslation } from "@l2r-front/l2r-i18n"
import { Campaign } from "@l2r-front/l2r-proptypes"
import { I18N_NAMESPACE, STATUS_DELIVERED, STATUS_IN_PROGRESS } from "@l2r-front/l2r-shared-campaign"
import { Typography } from "@l2r-front/l2r-ui"

import { CampaignDetails } from "../../components/CampaignDetails"
import { useCampaignDispatchContext, useCampaignStateContext } from "../../contexts/CampaignContext"
import { useCampaignDetailStateContext } from "../../contexts/CampaignDetailContext"
import { useCampaignEdit } from "../../hooks/mutations/useCampaignEdit"

import { CampaignDeliverModal } from "../CampaignDeliverModal"

import * as Styled from "./CampaignDetailSidebarContent.styled"

interface CampaignDetailSidebarContentProps {
    campaign: Campaign
}

export function CampaignDetailSidebarContent(props: CampaignDetailSidebarContentProps) {

    const { campaign } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const { viewMode } = useCampaignDetailStateContext()
    const [deliverModalOpen, setDeliverModalOpen] = useState(false)
    const { campaignFilters } = useCampaignStateContext()
    const { setCampaignFilters } = useCampaignDispatchContext()

    useEffect(() => {
        return () => {
            setCampaignFilters({})
        }
    }, [setCampaignFilters])

    const CampaignContentComponent = useMemo(() => {
        if (viewMode === "table") {
            return null
        }

        switch (campaign.campaign_type) {
            case API_MODULE_HORIZONTAL_SIGNING:
                return <HorizontalSignCampaignCatalog
                    campaign={campaign}
                    campaignFilters={campaignFilters}
                    setCampaignFilters={setCampaignFilters} />
            default:
                return null
        }
    }, [campaign, viewMode, campaignFilters, setCampaignFilters])

    const updatedInfo = useMemo(() => {
        if (!campaign.updated_at) {
            return null
        }

        const labelKey = campaign.status === STATUS_DELIVERED ? "containers.campaignDetailSidebar.deliverInfo" : "containers.campaignDetailSidebar.updateInfo"
        const responsable = campaign.updated_by ?? campaign.created_by ?? "Logiroad"

        return <Typography variant="caption">
            {
                t(I18N_NAMESPACE, labelKey, {
                    date: format(new Date(campaign.updated_at), "dd/MM/yyyy"),
                    user: responsable,
                })
            }
        </Typography>
    }, [campaign, t])

    const campaignMutation = useCampaignEdit(campaign.uuid, {}, {
        errorSnackbarMessage: t(I18N_NAMESPACE, "containers.campaignDetailSidebar.reopenCampaign.snackbar.error"),
        successSnackbarMessage: t(I18N_NAMESPACE, "containers.campaignDetailSidebar.reopenCampaign.snackbar.success"),
    })

    const handleStatusChange = useCallback((status: number) => {
        if (status === STATUS_DELIVERED) {
            setDeliverModalOpen(true)
        } else if (status === STATUS_IN_PROGRESS) {
            campaignMutation.mutate({ status })
        }
    }, [campaignMutation, setDeliverModalOpen])

    return <>
        <Styled.Container>
            <Styled.CampaignTitleContainer>
                <Styled.Color color={campaign.color} />
                <Typography variant="Desktop & Mobile/Heading/H4">
                    {campaign.name}
                </Typography>
            </Styled.CampaignTitleContainer>
            <Styled.StatusSelector campaign={campaign} onStatusChange={handleStatusChange} />
            <CampaignDetails campaign={campaign} />
            <Styled.ContentContainer>
                {CampaignContentComponent}
                {updatedInfo}
            </Styled.ContentContainer>
        </Styled.Container>
        {
            deliverModalOpen && <CampaignDeliverModal
                campaign={campaign}
                open={deliverModalOpen}
                onClose={() => setDeliverModalOpen(false)}
            />
        }
    </>
}